"use client";
import { useState, useRef, useEffect } from "react";

/**
 * Un componente que muestra texto y lo trunca después de un número específico de líneas,
 * mostrando un botón "Ver más" si el contenido excede el límite.
 *
 * @param {string} text El texto completo a mostrar.
 * @param {number} [clampLines=3] El número de líneas a mostrar antes de truncar.
 */
export default function ExpandableText({ text, clampLines = 3 }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    // La lógica de detección de desbordamiento sigue siendo válida.
    // Compara el scrollHeight (altura total del contenido) con
    // el clientHeight (altura visible del contenedor, que ahora está
    // limitada por 'line-clamp').
    if (!isExpanded && textRef.current) {
      // Usamos un timeout para asegurar que el DOM se haya renderizado
      // y las mediciones de altura sean correctas.
      const timeoutId = setTimeout(() => {
        if (textRef.current) {
          const needs = textRef.current.scrollHeight > textRef.current.clientHeight;
          setNeedsExpand(needs);
        }
      }, 0); // 0ms es suficiente para esperar al siguiente "tick" del navegador.

      return () => clearTimeout(timeoutId);
    }
    
    // Si está expandido, no hacemos nada para mantener 'needsExpand' en true
    // y así mostrar el botón "Ver menos".
  }, [text, isExpanded, clampLines]); // Añadimos clampLines a las dependencias

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  // Estilos para el estado contraído (con line-clamp)
  const clampStyle = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: clampLines,
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  // Estilos para el estado expandido (anula line-clamp)
  const expandedStyle = {
    display: "block",
    WebkitLineClamp: "none",
    overflow: "visible",
  };

  return (
    <div
      ref={containerRef}
      className="shadow-sm rounded-lg relative mt-5 p-5 text-left flex flex-col bg-white"
    >
      {/* Contenedor del texto */}
      <div
        ref={textRef}
        // Ya no usamos las clases de transición de altura,
        // ya que line-clamp no se anima suavemente como maxHeight.
        className="overflow-hidden"
        style={isExpanded ? expandedStyle : clampStyle}
      >
        <p className="whitespace-pre-line text-pretty">{text}</p>
      </div>

      {/* Botón solo si se necesita expandir */}
      {needsExpand && (
        <button
          onClick={toggleExpand}
          className="text-sm text-blue-600 hover:text-blue-800 self-end mt-2 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Contraer texto" : "Expandir texto"}
        >
          {isExpanded ? "Ver menos" : "Ver más"}
        </button>
      )}
    </div>
  );
}